import { buildClinicConfig } from "../../clinicConfig/build.mjs";

//#region src/tenants/hhb/clinic.ts
/**
* One object for every Azalea name that means Medicare FFS. The rows only
* collapse into one insurance record while their mapped fields are identical,
* so the keys share it rather than each carrying a copy that could drift.
*/
const AZALEA_MEDICARE_PLAN = {
	planName: "Medicare",
	payorId: "04412",
	payor: "Medicare",
	planType: "PPO",
	referralsRequirePriorAuth: false
};
/**
* HHB clinic / payor maps passed into Libretto jobs.
*
* Duplicate this object into other tenant files rather than importing it so
* Skylight/Demo can diverge without editing HHB.
*
* `payors` is the single declaration everything payor-shaped derives from: the
* Availity payer names, the portal routing, the insurance-modal dropdown, and
* the prior-auth and eligibility gates. Entries with neither `availityName` nor
* `priorAuth` are recordable only — staff can put them on a patient and no
* workflow will act on them, which is correct for the Texas payors HHB sees but
* we have not automated.
*
* Both Humana entities are listed on purpose. HHB's Availity account and its
* prior auths are keyed on "Humana Insurance Company"; bare "Humana" is a
* separate canonical payor that must never be folded into it, so staff need to
* be able to record either.
*
* Azalea is HHB's only EHR, so `azaleaPayorMappings` is its only payor map.
* Azalea falls through to the raw `ins_name` when a payor is unmapped (see
* `azaleaPullOpenReferrals.ts`).
*
* HHB's retired ECW map was the only writer of "Superior Health Plan Texas",
* which survives as a spelling in the registry because rows at rest still hold it.
*/
const hhbClinicConfig = buildClinicConfig({
	payors: [
		{
			payor: "BCBSTX",
			availityName: "BCBSTX",
			priorAuth: true
		},
		{
			payor: "Blue Cross Medicare Advantage",
			availityName: "Blue Cross Medicare Advantage",
			priorAuth: true
		},
		{
			payor: "Humana Insurance Company",
			availityName: "Humana",
			priorAuth: true
		},
		{
			payor: "UHC",
			portal: "uhc",
			priorAuth: true
		},
		{
			payor: "WellMed",
			portal: "uhc",
			priorAuth: true
		},
		{
			payor: "Wellpoint",
			availityName: "Wellpoint",
			priorAuth: true
		},
		{ payor: "Aetna" },
		{ payor: "Cigna" },
		{ payor: "Humana" },
		{ payor: "Medicare" },
		{ payor: "Superior Health Plan" }
	],
	athenaPayorMappings: {},
	azaleaPayorMappings: {
		"United Healthcare": {
			payorId: "87726",
			payor: "UHC"
		},
		"United HealthCare": {
			payorId: "87726",
			payor: "UHC"
		},
		"United HealthCare of all states": {
			payorId: "87726",
			payor: "UHC"
		},
		"Blue Cross and Blue Shield of Texas": {
			payorId: "00021",
			payor: "BCBSTX"
		},
		"BCBS of TX": {
			payorId: "00021",
			payor: "BCBSTX"
		},
		"Blue Cross Blue Shield Medicare Advantage MEDICARE ADVANTAGE PPO": {
			payorId: "66006",
			payor: "Blue Cross Medicare Advantage"
		},
		Wellpoint: {
			payorId: "WLPNT",
			payor: "Wellpoint",
			payorPhoneNumber: "8337312160",
			referralsRequirePriorAuth: false
		},
		"Wellpoint HMO": {
			payorId: "WLPNT",
			payor: "Wellpoint",
			payorPhoneNumber: "8337312160",
			referralsRequirePriorAuth: false
		},
		Medicare: AZALEA_MEDICARE_PLAN,
		"Novitas Solutions Part A": AZALEA_MEDICARE_PLAN
	},
	insuranceGroupToInfoMapping: { "HEALTHSELECT OF TEXAS": {
		planType: "HMO",
		planName: "HealthSelect of Texas",
		referralsRequired: true
	} }
});

//#endregion
export { hhbClinicConfig };